import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { subscriptionTrendQueryOptions } from "@/hooks/use-subscriptions";
import { isPaidPlan } from "./subscription-analytics-utils";
import type { SubscriptionTrendPoint } from "@/types/subscription";
import { Plan, Platform, SubscriptionCycle } from "@/types/enums";

type TrendMode = "paidTotal" | "plan" | "cycle" | "platform";

const PERIOD_OPTIONS = [
  { label: "7일", days: 7 },
  { label: "30일", days: 30 },
  { label: "90일", days: 90 },
] as const;

const MODE_OPTIONS: ReadonlyArray<{ label: string; mode: TrendMode }> = [
  { label: "유료 합계", mode: "paidTotal" },
  { label: "플랜별", mode: "plan" },
  { label: "주기별", mode: "cycle" },
  { label: "플랫폼별", mode: "platform" },
];

const PAID_PLAN_ORDER: Plan[] = [Plan.PLUS, Plan.PRO, Plan.PREMIUM];

const PLAN_COLOR: Record<Plan, string> = {
  [Plan.FREE]: "#9ca3af",
  [Plan.PRO_TRIAL]: "#a855f7",
  [Plan.STUDENT_PASS]: "#22c55e",
  [Plan.PLUS]: "#0ea5e9",
  [Plan.PRO]: "#3b82f6",
  [Plan.PREMIUM]: "#f59e0b",
};

const CYCLE_ORDER: SubscriptionCycle[] = [
  SubscriptionCycle.MONTHLY,
  SubscriptionCycle.YEARLY,
];

const CYCLE_LABEL: Record<SubscriptionCycle, string> = {
  [SubscriptionCycle.MONTHLY]: "월간",
  [SubscriptionCycle.YEARLY]: "연간",
};

const CYCLE_COLOR: Record<SubscriptionCycle, string> = {
  [SubscriptionCycle.MONTHLY]: "#3b82f6",
  [SubscriptionCycle.YEARLY]: "#10b981",
};

const PLATFORM_NONE_KEY = "NONE";
type PlatformKey = Platform | typeof PLATFORM_NONE_KEY;

const PLATFORM_KEYS: PlatformKey[] = [
  Platform.TOSS,
  Platform.IOS,
  Platform.ANDROID,
  Platform.STRIPE,
  PLATFORM_NONE_KEY,
];

const PLATFORM_LABEL: Record<PlatformKey, string> = {
  [Platform.TOSS]: "TOSS",
  [Platform.IOS]: "IOS",
  [Platform.ANDROID]: "ANDROID",
  [Platform.STRIPE]: "STRIPE",
  [PLATFORM_NONE_KEY]: "없음",
};

const PLATFORM_COLOR: Record<PlatformKey, string> = {
  [Platform.TOSS]: "#6366f1",
  [Platform.IOS]: "#71717a",
  [Platform.ANDROID]: "#22c55e",
  [Platform.STRIPE]: "#a855f7",
  [PLATFORM_NONE_KEY]: "#d4d4d8",
};

interface SeriesDescriptor {
  key: string;
  label: string;
  color: string;
}

type TrendRow = Record<string, string | number>;

function seriesForMode(mode: TrendMode): SeriesDescriptor[] {
  switch (mode) {
    case "paidTotal":
      return [{ key: "paidTotal", label: "유료 구독자", color: "#3b82f6" }];
    case "plan":
      return PAID_PLAN_ORDER.map((plan) => ({
        key: plan,
        label: plan,
        color: PLAN_COLOR[plan],
      }));
    case "cycle":
      return CYCLE_ORDER.map((cycle) => ({
        key: cycle,
        label: CYCLE_LABEL[cycle],
        color: CYCLE_COLOR[cycle],
      }));
    case "platform":
      return PLATFORM_KEYS.map((platformKey) => ({
        key: platformKey,
        label: PLATFORM_LABEL[platformKey],
        color: PLATFORM_COLOR[platformKey],
      }));
  }
}

// 모든 모드는 유료 구독자(PLUS/PRO/PREMIUM)만 집계한다.
function buildTrendRows(
  points: SubscriptionTrendPoint[],
  mode: TrendMode,
  series: SeriesDescriptor[]
): TrendRow[] {
  return points.map((point) => {
    const row: TrendRow = { date: point.date };
    for (const descriptor of series) {
      row[descriptor.key] = 0;
    }

    for (const item of point.breakdown) {
      if (!isPaidPlan(item.plan)) continue;

      const key =
        mode === "paidTotal"
          ? "paidTotal"
          : mode === "plan"
            ? item.plan
            : mode === "cycle"
              ? item.subscriptionCycle
              : (item.platform ?? PLATFORM_NONE_KEY);

      if (key in row) {
        row[key] = (row[key] as number) + item.count;
      }
    }

    return row;
  });
}

export function SubscriberTrendChart() {
  const [days, setDays] = useState<number>(30);
  const [mode, setMode] = useState<TrendMode>("paidTotal");

  const { data, isPending, isError } = useQuery(
    subscriptionTrendQueryOptions(days)
  );

  const series = useMemo(() => seriesForMode(mode), [mode]);
  const rows = useMemo(
    () => (data ? buildTrendRows(data, mode, series) : []),
    [data, mode, series]
  );

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <CardTitle>구독자 추이</CardTitle>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-1">
            {MODE_OPTIONS.map((option) => (
              <Button
                key={option.mode}
                size="sm"
                variant={mode === option.mode ? "default" : "outline"}
                onClick={() => setMode(option.mode)}
              >
                {option.label}
              </Button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            {PERIOD_OPTIONS.map((option) => (
              <Button
                key={option.days}
                size="sm"
                variant={days === option.days ? "default" : "outline"}
                onClick={() => setDays(option.days)}
              >
                {option.label}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-[360px] w-full">
          {isError ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              추이를 불러오지 못했습니다.
            </div>
          ) : isPending ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              불러오는 중…
            </div>
          ) : rows.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              아직 스냅샷이 없습니다. 매일 새벽 자동으로 기록됩니다.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={rows}
                margin={{ top: 16, right: 24, bottom: 8, left: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value: string) => value.slice(5)}
                  minTickGap={24}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    borderRadius: 8,
                    fontSize: 12,
                    border: "1px solid #e5e7eb",
                  }}
                />
                {series.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
                {series.map((descriptor) => (
                  <Line
                    key={descriptor.key}
                    type="monotone"
                    dataKey={descriptor.key}
                    name={descriptor.label}
                    stroke={descriptor.color}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
